import { Kitchen } from '@rta/rta-blueprint-library';
import { useContext, useState } from 'react';
import { useKeystoneSession } from '../auth/use-keystone-session';

import { AppContext, DesignActionTypes } from '@context';
import { clearDirty, selectNavigation, setAiRendering, setCanonical, setRawRendering, setSignature, type RenderingSource, useAppDispatch, useAppSelector } from '@state';
import { downscaleImage, getKitchenRenderSignature } from '@helpers';
import { patchBlueprintMetadata } from '@external';
import { useAppToast } from './use-app-toast.hook';
import { useDesign } from './use-design';

export type SetRenderingParams = {
	image: string;
	/** Kitchen of the design being captured. Defaults to the active design — pass it explicitly whenever the capture box is opened for a design other than the one loaded in the editor. */
	kitchen?: Kitchen | null;
	source?: RenderingSource;
};

export type UseSetRenderingReturn = {
	setRendering: (params: SetRenderingParams) => Promise<boolean>;
	isSaving: boolean;
};

/**
 * Single write path for "Set As Rendering": stages the capture on the in-memory blueprint so
 * every consumer (getDesignImage) is instantly correct, then persists it via its own API write —
 * independent of the design save — so the staleness guard clears without waiting on a save.
 */
export const useSetRendering = (): UseSetRenderingReturn => {
	const [, design] = useDesign();
	const dispatch = useAppDispatch();
	const { appDispatch } = useContext(AppContext);
	const appToast = useAppToast();
	const appNav = useAppSelector(selectNavigation);
	const { getToken } = useKeystoneSession();

	const [isSaving, setIsSaving] = useState(false);

	const setRendering = async (params: SetRenderingParams): Promise<boolean> => {
		const { image: rawDataUrl, kitchen: targetKitchen = null, source = 'raw' } = params;

		const kitchen = targetKitchen ?? design?.tdController?.kitchen ?? null;
		const blueprint = kitchen?.context?.blueprint ?? design?.blueprint ?? null;
		if (!blueprint || !appNav.email) return false;

		setIsSaving(true);
		try {
			const imageData = await downscaleImage(rawDataUrl);
			const signature = getKitchenRenderSignature(kitchen);

			blueprint.thumbnail = imageData;
			blueprint.metadata.renderingCapturedAt = new Date().toISOString();
			blueprint.metadata.renderingCanonical = source;
			blueprint.metadata.renderingKitchenSignature = signature;
			// Mirror the server's applyRenderings, which clears these when it stores a thumbnail —
			// otherwise canonicalRenderingPath keeps resolving to the PREVIOUS rendering's blob URL
			blueprint.metadata.renderingRawPath = '';
			blueprint.metadata.renderingAiPath = '';

			// The rendering slice is seeded per active blueprint, so mirroring a capture taken for a
			// different design would show that design's image in the active design's panel.
			const isActiveDesign = blueprint.id === design?.blueprint?.id;
			if (isActiveDesign) {
				if (source === 'ai') dispatch(setAiRendering(imageData));
				else dispatch(setRawRendering(imageData));
				dispatch(setCanonical(source));
				dispatch(setSignature(signature));
			}

			// thumbnail is an unpublished property, so consumers memoised on the designs array
			// (design list/tab/view thumbnails) would keep showing the previous rendering all session
			appDispatch({ type: DesignActionTypes.renderingUpdated, payload: { id: blueprint.id }});

			const designId = blueprint.submissionId ?? blueprint.id;
			const [success, error] = await patchBlueprintMetadata({
				customerEmail: appNav.email,
				designId,
				metadata: {
					renderingCanonical: source,
					renderingKitchenSignature: signature
				},
				thumbnail: imageData,
				getToken,
				mode: 'live'
			});

			if (success) {
				if (isActiveDesign) dispatch(clearDirty());
				appToast.success({ title: 'Rendering saved' });
				return true;
			}

			appToast.error({ title: 'Failed to save rendering', description: error ?? undefined });
			return false;
		} finally {
			setIsSaving(false);
		}
	};

	return { setRendering, isSaving };
};
